<template>
    <div class="table-gestore card p-3">
        <TableViewBo :columns="tableColumns" :items="questions" :pagination="pagination" :sort-field="sortField"
            :sort-direction="sortDirection" @page-change="$emit('page-change', $event)" @sort="$emit('sort', $event)"
            :show-search="false" :has-actions="false" :all-selected="allSelected" @select-all="toggleSelectAll"
            :selectable="!embedded">
            <template v-if="!embedded" #selected="{ item }">
                <input type="checkbox" :value="item.id" v-model="selectedQuestions" @change="onSelectionChange">
            </template>
            <template #id="{ item }">
                <a href="#" class="text-decoration-none"
                    @click.prevent="$emit('change-view', 'QuestionDetailExpertView', item.id)">
                    #{{ item.id }}
                </a>
            </template>
            <template #created_at="{ item }">
                <div style="text-align: center;">{{ formatDate(item.created_at) }}</div>
            </template>
            <template #sending_date="{ item }">{{ formatDate(item.sending_date) }}</template>
            <template #organization="{ item }">{{ item.organization }}</template>
            <template #region="{ item }">{{ item.region }}</template>
            <template #topics="{ item }">
                <div v-for="t in item.topics" :key="t.id" class="text-truncate-ellipsis">{{ t.title }}</div>
            </template>
            <template #object="{ item }">
                <div style="text-align: left;" :title="item.object">
                    {{ item.object.length > 30 ? item.object.slice(0, 30) + '...' : item.object }}
                </div>
            </template>
            <template #articles="{ item }">
                <div :title="item.articles.map(a => formatArticleTitle(a.title)).join(', ')"
                    class="text-truncate-ellipsis">
                    {{ truncatedArticles(item.articles) }}
                </div>
            </template>
            <template #state_id="{ item }" v-if="showStateColumn">
                <div :class="['state-badge', getStateClass(item.state_id?.trim().toUpperCase())]">
                    {{ getStateDisplay(item.state_id) }}
                </div>
            </template>
            <!-- Publish badge -->
            <template #publish_badge="{ item }" v-if="showStateColumn">
                <span v-if="item.state_id?.trim().toUpperCase() === 'ASSEVERATO'" class="publish-indicator"
                    :class="formatAsseverazioneState(item).published ? 'published' : 'not-published'"
                    :title="formatAsseverazioneState(item).published ? 'Pubblicato' : 'Non pubblicato'">
                    {{ formatAsseverazioneState(item).published ? '✓' : '✕' }}
                </span>
            </template>
            <template #empty>Nessun elemento trovato.</template>
        </TableViewBo>
    </div>
</template>

<script>
import TableViewBo from '../../../../components/common/TableViewBo.vue';
import { STATE_CONFIG } from '../../../../utils/stateConfig';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import SimpleModal from '../../../../components/common/SimpleModal.vue';

export default {
    name: 'UOCTable',
    components: { TableViewBo, Multiselect, SimpleModal },
    props: {
        questions: Array,
        pagination: Object,
        sortField: String,
        sortDirection: String,
        stateIcons: Object,
        currentFilters: Object,
        embedded: {
            type: Boolean,
            default: false
        },
        canAssign: {
            type: Boolean,
            default: false
        },
        showStateColumn: {
            type: Boolean,
            default: false
        },
    },
    data() {
        return {
            selectedQuestions: [],
            showConfirmModal: false,
            allSelected: false,
            modalQuestionId: null,
            isLoading: false,
        };

    },
    watch: {
        selectedQuestions(newVal) {
            this.allSelected = newVal.length === this.questions.length && this.questions.length > 0;
        },
        questions: {
            immediate: true,
            handler(newQuestions) {
                this.selectedQuestions = [];
                this.allSelected = false;
            }
        }
    },
    computed: {
        reassignableItems() {
            return this.questions.reduce((acc, item) => {
                acc[item.id] = ['INVIATO', 'ASSEGNATO', 'IN_LAVORAZIONE'].includes(item.state_id);
                return acc;
            }, {});
        },
        tableColumns() {
            const cols = [
                { key: 'selected', label: '', width: '2%' },
                { key: 'id', label: 'ID', sortable: true, width: '5%' },
                { key: 'object', label: 'OGGETTO', sortable: true, width: '10%' },
                { key: 'articles', label: 'ARTICOLI', width: '10%' },
                { key: 'organization', label: 'STAZ. APPALTANTE', sortable: true, width: '15%' },
                { key: 'region', label: 'REGIONE', sortable: true, width: '10%' },
                { key: 'created_at', label: 'DATA RICEZIONE', sortable: true, width: '10%' },
                { key: 'sending_date', label: 'DATA EVASIONE', sortable: true, width: '10%' },
            ];

            if (this.showStateColumn) {
                cols.push(
                    { key: 'state_id', label: 'STATO', sortable: true, width: '11%' },
                    { key: 'publish_badge', label: '', sortable: false, width: '5%' }
                );
            }

            return cols;
        }
    },
    methods: {
        formatAsseverazioneState(question) {
            if (!question || question.state_id !== 'ASSEVERATO') return null;

            return {
                label: 'ASSEVERATO',
                published: !!question.published,
            };
        },
        getStateDisplay(state) {
            if (!state) return '';

            const map = {
                'RISPOSTA_IN_BOZZA': 'BOZZA',
                'INVIATO': 'RICEVUTO',
                'ASSEGNATO': 'ASSEGNATO',
                'IN_LAVORAZIONE': 'IN LAVORAZIONE',
                'ASSEVERATO': 'ASSEVERATO',
                'PUBBLICATO': 'PUBBLICATO',
                'EVASO': 'EVASO',
                'ASSEGNATO A UOC': 'ASSEGNATO A UOC',
                'NON_ASSEVERATO': 'NON ASSEVERATO',
                'IN_ESAMINA_UOC': 'ASSEGNATO A UOC',
                'STORICO': 'STORICO',
            };

            return map[state?.trim().toUpperCase()] || state;
        },
        getStateClass(state) {
            switch (state) {
                case 'INVIATO': return 'state-inviato';
                case 'ASSEGNATO': return 'state-blu';
                case 'IN_LAVORAZIONE': return 'state-purple';
                case 'ASSEVERATO':
                case 'PUBBLICATO':
                case 'EVASO': return 'state-verde';
                case 'IN_ESAMINA_UOC': return 'state-inviato';
                case 'RISPOSTA_IN_BOZZA': return 'state-bozza';
                case 'NON_ASSEVERATO': return 'state-rosso';
                case 'STORICO': return 'state-storico';
                default: return '';
            }
        },
        initials(user) {
            if (!user) return '';
            return `${user.name[0]}${user.surname[0]}`.toUpperCase();
        },
        goToDetail(id) {
            this.$router.push({
                name: 'questionsexpertdetail',
                params: { id },
                query: { filters: JSON.stringify(this.currentFilters) }
            })
                .then(() => {
                    console.log('Navigazione riuscita');
                })
                .catch(err => {
                    console.error('Errore nella navigazione:', err);
                });
        },
        toggleSelectAll(checked) {
            this.allSelected = checked;
            this.selectedQuestions = checked ? this.questions.map(q => q.id) : [];
            this.$emit('update:selected-questions', this.selectedQuestions);
        },
        formatArticleTitle(title) {
            if (!title) return '—';

            const match = title.match(/^Art\.\s*\d+/);
            if (match) return match[0];

            if (/^Articolo\s+\d+/i.test(title)) return title;

            return title;
        },

        truncatedArticles(articles) {
            if (!articles || !articles.length) return '—';

            const formatted = articles.map(a => this.formatArticleTitle(a.title));
            if (formatted.length <= 2) {
                return formatted.join(', ');
            } else {
                return formatted.slice(0, 2).join(', ') + ', ...';
            }
        },
        onSelectionChange() {
            this.allSelected = this.selectedQuestions.length === this.questions.length;
            this.$emit('update:selected-questions', this.selectedQuestions);
        },
        formatDate(d) {
            if (!d) return '-';
            const date = new Date(d);
            return date.toLocaleDateString('it-IT');
        }
    }
};
</script>

<style scoped>
.table-gestore {
    height: 100%;
    display: flex;
    flex-direction: column;
}

/* Scroll verticale solo qui */
.table-gestore .table-wrapper {
    flex: 1;
    overflow-y: auto;
}

/* Stati colorati */
.state-badge {
    padding: 0.2rem 0.5rem;
    border-radius: 0.25rem;
    display: inline-block;
    min-width: 10rem;
    text-align: center;
    font-weight: bold;
    font-size: 0.85rem;
}

.state-inviato {
    color: white;
    border: 1px solid gray;
    background-color: #ffc107;
}

.state-bozza {
    color: white;
    border: 1px solid white;
    background-color: gray;
}

.state-purple {
    color: white;
    border: 1px solid purple;
    background-color: purple;
}

.state-verde {
    color: white;
    border: 1px solid green;
    background-color: green;
}

.state-rosso {
    color: red;
    border: 1px solid red;
    background-color: #f8d7da;
}

.state-blu {
    color: white;
    border: 1px solid blue;
    background-color: blue;
}

.avatar-circle.empty {
    background: #fff;
    border-color: #6c757d;
    color: #6c757d;
}

.avatar-circle {
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;

    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;

    background-color: #ffffff;
    border: 1px solid #ced4da;
    color: #000000;
    flex-shrink: 0;
}

::v-deep(.multiselect__content-wrapper) {
    z-index: 2000 !important;
}

.loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    z-index: 2000;
}

.loading-text {
    margin-top: 1rem;
    font-weight: bold;
    color: #0d6efd;
}

.avatar-circle {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background-color: #6c757d;
    /* grigio bootstrap */
    color: #fff;
    font-weight: bold;
    font-size: 0.8rem;
    text-transform: uppercase;
}

.table-responsive>.table {
    overflow: visible;
}

.table-gestore .dropdown-menu {
    /**top: 80% !important;*/
    margin-top: var(--bs-dropdown-spacer) !important;
    min-width: 19rem;
    /**left: auto !important;*/
}

.expert-dropdown-menu {
    max-height: 200px;
    overflow-y: auto;
}

.full-width-dropdown {
    width: 100%;
    text-align: left;
    justify-content: flex-start;
}

.active-expert {
    background-color: #0d6efd;
    color: white;
}

.active-expert .expert-name {
    font-weight: bold;
}

.state-storico {
    color: white;
    border: 1px solid black;
    background-color: black;
}

.published {
    color: green;
}

.not-published {
    color: red;
}

::v-deep(th) {
    text-align: center;
}

.publish-indicator {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    font-size: 16px;
    font-weight: bold;
    color: white;
}

.published {
    background-color: green;
}

.not-published {
    background-color: red;
}


/** .table-gestore .table-scroll-wrapper .table-row-hover {
  max-height: 300px;
  overflow-y: hidden;
  position: relative;
}*/
</style>
